import React, { useState, useRef } from 'react';
import { styled } from '@compiled/react';
import { Editor, type EditorActions } from '@atlaskit/editor-core';
import TextArea from '@atlaskit/textarea';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { TranslationLogicalId } from '@atlassian/jira-servicedesk-language-settings-translations-resource/src/services/get-data/types.tsx';
import { useManageTranslationsActions } from '../../../controllers/manage-translations/index.tsx';
import type { VariableResponse } from '../../../services/notification-variables/index.tsx';
import { EditorDropdownMenu } from './editor-dropdown';
import messages from './messages';
import { parseToADF, parseToWikiMarkup } from './utils';

const NotificationEditor = ({
	content,
	variableGroups,
	safeNotificationSettingsEnabled,
	isUpToDate,
	isReadOnly,
	logicalId,
}: {
	content: string;
	variableGroups: VariableResponse[];
	safeNotificationSettingsEnabled: boolean;
	isUpToDate: boolean;
	isReadOnly: boolean;
	logicalId: TranslationLogicalId;
}) => {
	const { formatMessage } = useIntl();
	const [editorActions, setEditorActions] = useState<EditorActions>();
	const onEditorReady = (actions: EditorActions) => {
		setEditorActions(actions);
	};
	const containerRef = useRef<HTMLDivElement>(null);

	const { updateFields } = useManageTranslationsActions();

	const handleChange = async () => {
		const value = await editorActions?.getValue();
		const fieldContent = parseToWikiMarkup(value);
		const payload = {
			fieldContent,
			fieldLogicalId: logicalId,
		};

		updateFields(payload);
	};

	return isReadOnly ? (
		<TextArea isDisabled={isReadOnly} isReadOnly={isReadOnly} defaultValue={content} />
	) : (
		<EditorWrapper
			data-testid="language-settings.ui.generic-panel.notification-editor.editor-wrapper"
			showBorder={!isUpToDate}
			ref={containerRef}
		>
			<Editor
				disabled={false}
				defaultValue={parseToADF(content)}
				onEditorReady={onEditorReady}
				onChange={handleChange}
				appearance="comment"
				allowRule
				allowTables={{
					allowControls: true,
				}}
				allowPanel
				allowTextColor
				popupsMountPoint={containerRef.current || undefined}
				popupsBoundariesElement={containerRef.current || undefined}
				primaryToolbarComponents={[
					<EditorDropdownMenu
						key={0}
						variableGroups={variableGroups}
						safeNotificationSettingsEnabled={safeNotificationSettingsEnabled}
						editorActions={editorActions}
					/>,
				]}
				media={{
					allowMediaSingle: true,
				}}
				placeholder={formatMessage(messages.fieldPlaceholder)}
			/>
		</EditorWrapper>
	);
};

export default NotificationEditor;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EditorWrapper = styled.div<{ showBorder: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	borderLeft: ({ showBorder }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		showBorder ? `5px solid ${token('color.border.warning', colors.Y200)}` : 'none',
	borderRadius: '5px',
});
